import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Spin, message, Typography, Card, Space, Tooltip, Button } from 'antd';
import { RefreshCw } from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../../../shared/services/api/axiosInstance';
import EnhancedVisualization from '../../../shared/components/visualization/EnhancedVisualization';

const { Title, Text } = Typography;

// Updated Design System styles - Matching DashboardPage
const styles = {
  primaryBackground: "#ffffff", // White
  secondaryBackground: "#DDE2EE", // Light blue-grey
  textPrimary: "#0f172a", // Black for important elements
  textSecondary: "#666666", // Grey for regular text
  footerBackground: "#000000", // Black background
  footerText: "#ffffff", // White text for footer
  borderColor: "#DDE2EE", // Light blue-grey for borders
  cardBg: "#ffffff", // White card background
  cardShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
  cardShadowHover: "0 4px 16px rgba(0, 0, 0, 0.1)",
  // Typography
  headingFont: "Raleway, sans-serif",
  bodyFont: "Raleway, sans-serif"
};

const brandColor = '#1d0c46';

// Add CSS for infinite canvas widgets - Matching DashboardPage
const canvasStyles = `
  .canvas-widget {
    position: absolute;
    cursor: default;
    transition: none;
    user-select: none;
  }
  .canvas-widget:hover {
    box-shadow: none !important;
  }
  /* Remove Ant Design Card hover effects in view mode */
  .view-mode .ant-card {
    border-radius: 0 !important;
    transition: none !important;
  }
  .view-mode .ant-card:hover {
    box-shadow: none !important;
    transform: none !important;
  }
  .view-mode .canvas-background {
    cursor: default;
    border: none !important;
    border-width: 0 !important;
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = canvasStyles;
  document.head.appendChild(styleElement);
}

const PublicDashboardPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  
  // State management
  const [dashboard, setDashboard] = useState(null);
  const [dashboardName, setDashboardName] = useState('');
  const [widgets, setWidgets] = useState([]);
  const [textWidgets, setTextWidgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [widgetData, setWidgetData] = useState({});
  const [executingWidgets, setExecutingWidgets] = useState(new Set());
  
  // Infinite canvas state
  const [widgetPositions, setWidgetPositions] = useState({});
  const [widgetSizes, setWidgetSizes] = useState({});
  const [textPositions, setTextPositions] = useState({});
  const [textSizes, setTextSizes] = useState({});
  
  const canvasRef = useRef(null);
  const canvasContainerRef = useRef(null);
  
  const [canvasWidth, setCanvasWidth] = useState(1280);
  const [canvasHeight, setCanvasHeight] = useState(900);
  const BOTTOM_PADDING_PX = 48;
  
  // Offset to center the entire layout in public view
  const [layoutOffsetX, setLayoutOffsetX] = useState(0);

  // Load public dashboard
  const loadPublicDashboard = useCallback(async () => {
    if (!token) {
      message.error('Invalid dashboard link');
      navigate('/');
      return;
    }
    
    try {
      setLoading(true);
      
      const response = await axiosInstance.get(`/dashboards/public/${token}`);
      const dashboardData = response.data.data;
      
      if (!dashboardData) {
        message.error('Dashboard not found or expired');
        navigate('/');
        return;
      }
      
      setDashboard(dashboardData);
      setDashboardName(dashboardData.name);
      // Dedupe by widgetId/textId so saved schema is single source of truth; preserve order (first occurrence)
      const rawWidgets = dashboardData.widgets || [];
      const seenWidgetIds = new Set();
      const dedupedWidgets = rawWidgets.filter((w) => {
        const id = w.widgetId ?? w.id;
        if (seenWidgetIds.has(id)) return false;
        seenWidgetIds.add(id);
        return true;
      });
      const rawText = dashboardData.textWidgets || [];
      const seenTextIds = new Set();
      const dedupedTextWidgets = rawText.filter((t) => {
        const id = t.textId ?? t.id;
        if (seenTextIds.has(id)) return false;
        seenTextIds.add(id);
        return true;
      });
      setWidgets(dedupedWidgets);
      setTextWidgets(dedupedTextWidgets);
      
      // Extract positions and sizes from widgets - Matching DashboardPage
      const positions = {};
      const sizes = {};
      dedupedWidgets.forEach((widget) => {
        const id = widget.widgetId ?? widget.id;
        positions[id] = widget.position || { x: 0, y: 0 };
        sizes[id] = widget.size || { width: 600, height: 450 };
      });
      setWidgetPositions(positions);
      setWidgetSizes(sizes);
      
      const textPos = {};
      const textSz = {};
      dedupedTextWidgets.forEach((textWidget) => {
        const textId = textWidget.textId;
        textPos[textId] = textWidget.position || { x: 0, y: 0 };
        textSz[textId] = textWidget.size || { width: 300, height: 100 };
      });
      setTextPositions(textPos);
      setTextSizes(textSz);
      
      // Apply saved canvas height if available - Matching DashboardPage
      if (dashboardData.settings?.totalCanvasHeight) {
        setCanvasHeight(dashboardData.settings.totalCanvasHeight);
      }
      
      // Execute queries for all widgets (use deduped list)
      dedupedWidgets.forEach((widget) => {
        const savedQuery = widget.savedQueryData || widget.savedQuery;
        const wid = widget.widgetId ?? widget.id;
        if (savedQuery && savedQuery.sqlQuery) {
          executeWidgetQuery(wid, savedQuery);
        }
      });
      
    } catch (error) {
      console.error('Error loading public dashboard:', error);
      message.error('Failed to load dashboard. It may have expired or been removed.');
      navigate('/');
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => {
    loadPublicDashboard();
  }, [loadPublicDashboard]);

  // Compute bounding box of all widgets to determine layout width
  const computeLayoutBounds = useCallback(() => {
    if (widgets.length === 0 && textWidgets.length === 0) {
      return null;
    }

    let minX = Infinity;
    let maxX = -Infinity;

    // Find bounds from widgets
    widgets.forEach((widget) => {
      if (widget.isVisible === false) return;
      const wid = widget.widgetId ?? widget.id;
      const position = widgetPositions[wid] || widget.position || { x: 0, y: 0 };
      const size = widgetSizes[wid] || widget.size || { width: 600, height: 450 };
      if (!position || !size) return;

      minX = Math.min(minX, position.x || 0);
      maxX = Math.max(maxX, (position.x || 0) + (size.width || 600));
    });

    // Find bounds from text widgets
    textWidgets.forEach((textWidget) => {
      if (textWidget.isVisible === false) return;
      const tid = textWidget.textId ?? textWidget.id;
      const position = textPositions[tid] || textWidget.position || { x: 0, y: 0 };
      const size = textSizes[tid] || textWidget.size || { width: 300, height: 100 };
      if (!position || !size) return;

      minX = Math.min(minX, position.x || 0);
      maxX = Math.max(maxX, (position.x || 0) + (size.width || 300));
    });

    if (minX === Infinity || maxX === -Infinity) {
      return null;
    }

    return {
      layoutWidth: maxX - minX,
      minX: minX
    };
  }, [widgets, textWidgets, widgetPositions, widgetSizes, textPositions, textSizes]);

  // Measure container width to set fixed canvas width = container width - Matching DashboardPage
  useEffect(() => {
    if (!canvasContainerRef.current) return;

    const measure = () => {
      if (canvasContainerRef.current) {
        const containerWidth = canvasContainerRef.current.clientWidth;
        const w = Math.floor(containerWidth);
        if (w > 0 && w !== canvasWidth) {
          setCanvasWidth(w);
        }
      }
    };

    measure();

    const resizeObserver = new ResizeObserver(() => {
      measure();
    });

    if (canvasContainerRef.current) {
      resizeObserver.observe(canvasContainerRef.current);
    }

    window.addEventListener('resize', measure);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [canvasWidth]);

  // Calculate offset to center the layout in public view
  useEffect(() => {
    if (!canvasContainerRef.current) return;

    const bounds = computeLayoutBounds();
    if (!bounds) {
      setLayoutOffsetX(0);
      return;
    }

    const containerWidth = canvasContainerRef.current.clientWidth;
    const layoutCenter = bounds.minX + bounds.layoutWidth / 2;
    const viewportCenter = containerWidth / 2;

    // Calculate offset to center the layout
    const offset = viewportCenter - layoutCenter;
    setLayoutOffsetX(offset);
  }, [
    widgets,
    textWidgets,
    widgetPositions,
    widgetSizes,
    textPositions,
    textSizes,
    computeLayoutBounds,
    canvasWidth
  ]);

  // Recompute canvas height based on bottom-most widget - Matching DashboardPage
  const recomputeCanvasHeight = useCallback(() => {
    let bottom = 0;
    Object.keys(widgetPositions).forEach((id) => {
      const pos = widgetPositions[id];
      const sz = widgetSizes[id] || { width: 600, height: 450 };
      if (!pos) return;
      bottom = Math.max(bottom, (pos.y || 0) + (sz.height || 0));
    });
    Object.keys(textPositions).forEach((id) => {
      const pos = textPositions[id];
      const size = textSizes[id] || { width: 300, height: 100 };
      if (!pos) return;
      bottom = Math.max(bottom, (pos.y || 0) + (size.height || 0));
    });
    const next = Math.max(600, bottom + BOTTOM_PADDING_PX);
    if (next !== canvasHeight) setCanvasHeight(next);
  }, [widgetPositions, widgetSizes, textPositions, textSizes, canvasHeight]);

  useEffect(() => {
    recomputeCanvasHeight();
  }, [recomputeCanvasHeight]);

  // Execute query for a widget (using public endpoint)
  const executeWidgetQuery = useCallback(async (widgetId, savedQuery) => {
    if (!savedQuery || !savedQuery.sqlQuery) {
      console.error('No SQL query found for widget:', widgetId);
      return;
    }

    try {
      setExecutingWidgets(prev => new Set([...prev, widgetId]));
      
      const response = await axiosInstance.post('/query/execute/public', {
        publicToken: token,
        widgetId: widgetId,
        savedQueryId: savedQuery.queryId || savedQuery._id
      });
      
      setWidgetData(prev => ({
        ...prev,
        [widgetId]: {
          data: response.data.data || [],
          columns: response.data.columns || [],
          config: savedQuery.visualizationConfig || {}
        }
      }));
      
    } catch (error) {
      console.error('Error executing query for widget:', widgetId, error);
      setWidgetData(prev => ({
        ...prev,
        [widgetId]: {
          error: error.response?.data?.message || 'Failed to execute query',
          data: [],
          columns: [],
          config: savedQuery.visualizationConfig || {}
        }
      }));
    } finally {
      setExecutingWidgets(prev => {
        const newSet = new Set(prev);
        newSet.delete(widgetId);
        return newSet;
      });
    }
  }, [token]);

  // Render widget - pixel-perfect match to internal dashboard view mode
  const renderWidget = (widget) => {
    if (widget.isVisible === false) return null;
    const widgetId = widget.widgetId ?? widget.id;
    const savedQuery = widget.savedQueryData || widget.savedQuery;
    const widgetQueryData = widgetData[widgetId];
    const isExecuting = executingWidgets.has(widgetId);
    const position = widgetPositions[widgetId] || widget.position || { x: 100, y: 100 };
    const size = widgetSizes[widgetId] || widget.size || { width: 600, height: 450 };

    // Handle missing or deleted query
    if (!savedQuery) {
      return (
        <div 
          key={widgetId}
          data-widget-id={widgetId}
          className="canvas-widget"
          style={{ 
            position: 'absolute',
            left: position.x + layoutOffsetX,
            top: position.y,
            width: `${size.width}px`,
            height: `${size.height}px`,
            display: 'flex', 
            flexDirection: 'column',
            overflow: 'hidden',
            zIndex: 1,
            cursor: 'default'
          }}
        >
          <Card
            style={{ 
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: styles.cardBg,
              borderColor: '#ef4444',
              boxShadow: styles.cardShadow,
              borderRadius: 0
            }}
            title={
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-red-500" style={{ 
                  fontFamily: styles.bodyFont,
                  fontWeight: 500
                }}>Query Not Found</span>
              </div>
            }
          >
            <div className="flex items-center justify-center h-full text-red-500">
              <div className="text-center">
                <div className="text-sm font-medium">Query Deleted or Not Found</div>
                <div className="text-xs mt-1 text-[#475569]">The query associated with this widget has been deleted or is no longer accessible.</div>
              </div>
            </div>
          </Card>
        </div>
      );
    }

    return (
      <div 
        key={widgetId}
        data-widget-id={widgetId}
        className="canvas-widget"
        style={{ 
          position: 'absolute',
          left: position.x + layoutOffsetX,
          top: position.y,
          width: `${size.width}px`,
          height: `${size.height}px`,
          display: 'flex', 
          flexDirection: 'column',
          overflow: 'hidden',
          zIndex: 1,
          cursor: 'default'
        }}
      >
        <Card
          style={{ 
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: styles.cardBg,
            borderColor: styles.borderColor,
            boxShadow: styles.cardShadow,
            borderRadius: 0
          }}
          title={
            <div className="flex items-center justify-between w-full">
              <div className="flex flex-col min-w-0 flex-1">
                {(() => {
                  const isTable = savedQuery.visualizationType === 'Table';
                  const heading = isTable
                    ? (widget.config?.tableName || widget.config?.title || savedQuery.name || '')
                    : (widget.config?.visualizationName || widget.config?.title || savedQuery.name || '');
                  const subheading = isTable
                    ? (widget.config?.tableDescription || widget.config?.visualizationDescription || '')
                    : (widget.config?.visualizationDescription || widget.config?.tableDescription || '');
                  return (
                    <>
                      {heading && (
                        <span className="text-sm font-medium" style={{
                          color: styles.textPrimary,
                          fontFamily: styles.bodyFont,
                          fontWeight: 500
                        }}>{heading}</span>
                      )}
                      {subheading && (
                        <span className="text-xs mt-1" style={{
                          color: styles.textSecondary,
                          fontFamily: styles.bodyFont,
                          fontWeight: 400
                        }}>{subheading}</span>
                      )}
                      {!heading && !subheading && (
                        <span className="text-sm font-medium" style={{
                          color: styles.textPrimary,
                          fontFamily: styles.bodyFont,
                          fontWeight: 500
                        }}>{savedQuery.name || 'Widget'}</span>
                      )}
                    </>
                  );
                })()}
              </div>
              <Space size="small" className="flex-shrink-0">
                <Tooltip title="Refresh data">
                  <Button
                    type="text"
                    size="small"
                    icon={<RefreshCw size={14} />}
                    onClick={() => executeWidgetQuery(widgetId, savedQuery)}
                    loading={isExecuting}
                    style={{ color: styles.textPrimary }}
                  />
                </Tooltip>
              </Space>
            </div>
          }
          styles={{
            body: {
              flex: 1,
              padding: savedQuery.visualizationType === 'KPI' ? '0px' : '12px',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            },
            header: {
              borderRadius: 0
            }
          }}
        >
          <div style={{ 
            flex: 1, 
            width: '100%', 
            height: '100%', 
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {isExecuting ? (
              <div className="flex items-center justify-center h-full">
                <Spin size="large" />
              </div>
            ) : widgetQueryData?.error ? (
              <div className="flex items-center justify-center h-full text-red-500">
                <div className="text-center">
                  <div className="text-sm font-medium">Error</div>
                  <div className="text-xs mt-1">{widgetQueryData.error}</div>
                </div>
              </div>
            ) : widgetQueryData?.data ? (
              <div style={{ 
                flex: 1,
                width: '100%', 
                height: '100%', 
                overflow: savedQuery.visualizationType === 'KPI' ? 'hidden' : 'auto',
                display: 'flex',
                flexDirection: 'column',
                margin: savedQuery.visualizationType === 'KPI' ? '0' : undefined
              }}>
                <EnhancedVisualization
                  type={savedQuery.visualizationType || 'Table'}
                  data={widgetQueryData.data}
                  columns={widgetQueryData.columns}
                  config={widgetQueryData.config || savedQuery.visualizationConfig || {}}
                  height={size.height - 80}
                  width={size.width}
                />
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-[#475569]">
                <div className="text-center">
                  <div className="text-sm">No data available</div>
                </div>
              </div>
            )}
          </div>
        </Card>
      </div>
    );
  };

  // Render text widget - same layout and formatting as internal dashboard (preserve HTML/markdown)
  const renderTextWidget = (textWidget) => {
    if (textWidget.isVisible === false) return null;
    const textId = textWidget.textId ?? textWidget.id;
    const position = textPositions[textId] || textWidget.position || { x: 100, y: 100 };
    const size = textSizes[textId] || textWidget.size || { width: 300, height: 100 };
    const content = textWidget.text ?? '';

    return (
      <div 
        key={textId}
        data-text-id={textId}
        className="canvas-widget"
        style={{ 
          position: 'absolute',
          left: position.x + layoutOffsetX,
          top: position.y,
          width: `${size.width}px`,
          height: `${size.height}px`,
          display: 'flex', 
          alignItems: 'center',
          justifyContent: textWidget.textAlign || 'flex-start',
          overflow: 'hidden',
          zIndex: 1,
          cursor: 'default',
          padding: '8px',
          backgroundColor: 'transparent',
          border: '1px dashed transparent',
          borderRadius: 0
        }}
      >
        <div
          className="public-dashboard-text-content"
          style={{
            fontSize: textWidget.fontSize ?? '14px',
            color: textWidget.color ?? styles.textPrimary,
            fontFamily: textWidget.fontFamily ?? styles.bodyFont,
            fontWeight: textWidget.fontWeight ?? 400,
            textAlign: textWidget.textAlign || 'left',
            width: '100%',
            wordWrap: 'break-word',
            lineHeight: 1.2
          }}
          dangerouslySetInnerHTML={content ? { __html: content } : undefined}
        />
      </div>
    );
  };

  return (
    <div className="flex h-screen overflow-hidden relative view-mode" style={{ 
      fontFamily: styles.bodyFont,
      backgroundColor: styles.primaryBackground,
      maxWidth: '100vw'
    }}>
      {/* Main Content Area - Y-axis scroll only; no horizontal scroll */}
      <div className="flex flex-col w-full h-screen relative z-10 min-w-0 overflow-x-hidden" style={{ width: '100%', maxWidth: '100%' }}>
        <div className="flex-1 pt-6 overflow-y-auto overflow-x-hidden min-w-0" style={{ width: '100%', maxWidth: '100%' }}>
          <Spin spinning={loading}>
            <div className="h-full relative">
              {/* Header - Matching DashboardPage view mode */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 min-w-0 gap-4">
                <div className="flex items-center gap-4 min-w-0 flex-1">
                  <Title level={4} className="mb-0 truncate" style={{ 
                    color: brandColor,
                    fontFamily: styles.headingFont,
                    fontWeight: 600
                  }}>
                    {dashboardName || 'Public Dashboard'}
                  </Title>
                  <Text type="secondary" className="text-sm whitespace-nowrap hidden sm:block" style={{ 
                    color: styles.textSecondary,
                    fontFamily: styles.bodyFont,
                    fontWeight: 400
                  }}>
                    This is a read-only public view
                  </Text>
                </div>
              </div>
              
              {/* Canvas - read-only, Y scroll only; layout from saved schema */}
              <div 
                className="relative overflow-x-hidden canvas-background view-mode"
                ref={canvasContainerRef}
                style={{ 
                  backgroundColor: styles.cardBg,
                  borderColor: 'transparent',
                  borderWidth: 0,
                  boxShadow: 'none',
                  overflowX: 'hidden',
                  overflowY: 'auto',
                  maxHeight: 'calc(100vh - 100px)',
                  borderRadius: 0,
                  width: '100%',
                  maxWidth: '100%',
                  minWidth: 0
                }}
              >
                <div
                  ref={canvasRef}
                  style={{
                    width: '100%',
                    maxWidth: '100%',
                    minWidth: 0,
                    height: `${canvasHeight}px`,
                    position: 'relative',
                    margin: 0,
                    boxSizing: 'border-box'
                  }}
                >
                  {(widgets.length > 0 || textWidgets.length > 0) && (
                    <div style={{ position: 'relative', width: '100%', height: '100%', minWidth: 0 }}>
                      {widgets.filter((w) => w.isVisible !== false).map((widget) => renderWidget(widget))}
                      {textWidgets.filter((t) => t.isVisible !== false).map((textWidget) => renderTextWidget(textWidget))}
                    </div>
                  )}
                  {widgets.length === 0 && textWidgets.length === 0 && !loading && (
                    <div 
                      className="absolute inset-0 flex items-center justify-center z-10"
                      style={{
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        backdropFilter: 'blur(2px)'
                      }}
                    >
                      <div className="text-center p-8 border-2 border-dashed" style={{
                        backgroundColor: styles.cardBg,
                        borderColor: styles.borderColor,
                        maxWidth: '400px',
                        boxShadow: 'none',
                        borderRadius: 0
                      }}>
                        <Text style={{ 
                          color: styles.textSecondary,
                          fontFamily: styles.bodyFont,
                          fontSize: '14px'
                        }}>
                          No widgets to display
                        </Text>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Spin>
        </div>
      </div>
    </div>
  );
};

export default PublicDashboardPage;

